import { ChevronsLeft, ChevronsRight, X } from 'lucide-react';
import { NavLink } from 'react-router';

import { Logo } from '../components/Logo';
import { useAuth } from '../features/auth/AuthProvider';
import { productName, productVersion } from './brand';
import { navSections, visibleItems } from './navigation';

interface Props {
  collapsed: boolean;
  /** Collapses the rail, or closes the menu on a phone. */
  onToggle: () => void;
  /** On a phone the menu is a drawer over the page: always expanded, closed by its button. */
  mobile?: boolean;
  /** Called when a link is followed (the phone drawer closes). */
  onNavigate?: () => void;
}

export function Sidebar({ collapsed, onToggle, mobile = false, onNavigate }: Props) {
  const { can, session } = useAuth();
  const clientPerson = Boolean(session?.user.client_id);
  const platform = Boolean(session?.tenant.platform);
  const sections = navSections
    .map((s) => ({ ...s, items: visibleItems(s.items, can, clientPerson, platform) }))
    .filter((s) => s.items.length > 0);
  return (
    <aside
      className={`flex shrink-0 flex-col border-r border-line bg-surface ${
        mobile ? 'h-full w-full' : `transition-[width] duration-150 ${collapsed ? 'w-14' : 'w-58'}`
      }`}
    >
      <div className="flex h-12 items-center gap-2.5 border-b border-line px-3.5">
        <Logo className="size-7" />
        {!collapsed && (
          <div className="min-w-0 leading-none">
            <div className="text-[13px] font-semibold tracking-tight text-fg">{productName}</div>
            <div className="mt-0.5 text-2xs text-fg-subtle">
              Video Management · v{productVersion}
            </div>
          </div>
        )}
      </div>

      <nav aria-label="Navegação principal" className="flex-1 overflow-y-auto px-2 py-3">
        {sections.map((section) => (
          <div key={section.label} className="mb-4 last:mb-0">
            {collapsed ? (
              <div aria-hidden="true" className="mx-2 mb-2 h-px bg-line first:hidden" />
            ) : (
              <div className="mb-1 px-2 text-2xs font-medium tracking-wide text-fg-subtle uppercase">
                {section.label}
              </div>
            )}
            <ul className="space-y-0.5">
              {section.items.map(({ to, label, icon: Icon }) => (
                <li key={to}>
                  <NavLink
                    to={to}
                    onClick={onNavigate}
                    title={collapsed ? label : undefined}
                    aria-label={collapsed ? label : undefined}
                    className={({ isActive }) =>
                      `group relative flex items-center gap-2.5 rounded-md px-2 text-[13px] transition-colors ${
                        mobile ? 'h-11' : 'h-8'
                      } ${
                        isActive
                          ? 'bg-accent-soft font-medium text-fg'
                          : 'text-fg-muted hover:bg-surface-hover hover:text-fg'
                      }`
                    }
                  >
                    {({ isActive }) => (
                      <>
                        {isActive && (
                          <span
                            aria-hidden="true"
                            className="absolute inset-y-1.5 -left-2 w-0.5 rounded-r bg-accent"
                          />
                        )}
                        <Icon
                          aria-hidden="true"
                          className={`size-4 shrink-0 ${isActive ? 'text-accent' : ''}`}
                          strokeWidth={1.75}
                        />
                        {!collapsed && <span className="truncate">{label}</span>}
                      </>
                    )}
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>

      <div className="border-t border-line p-2">
        <button
          type="button"
          onClick={onToggle}
          aria-label={
            mobile ? 'Fechar menu' : collapsed ? 'Expandir menu lateral' : 'Recolher menu lateral'
          }
          aria-expanded={mobile ? undefined : !collapsed}
          className={`flex w-full items-center gap-2.5 rounded-md px-2 text-[13px] text-fg-subtle hover:bg-surface-hover hover:text-fg ${
            mobile ? 'h-11' : 'h-8'
          }`}
        >
          {mobile ? (
            <>
              <X aria-hidden="true" className="size-4" strokeWidth={1.75} />
              <span>Fechar menu</span>
            </>
          ) : collapsed ? (
            <ChevronsRight aria-hidden="true" className="size-4" strokeWidth={1.75} />
          ) : (
            <>
              <ChevronsLeft aria-hidden="true" className="size-4" strokeWidth={1.75} />
              <span>Recolher</span>
            </>
          )}
        </button>
      </div>
    </aside>
  );
}
